import { LowStockIcon } from '@jouzen/ecom-components/icons/LowStockIcon';
import { BodyLink } from '@jouzen/ecom-components/v3/BodyLink';
import {
  LOW_STOCK,
  OUT_OF_STOCK,
} from '@jouzen/ecom-components/v3/consts/stockStatus';
import type { StockStatus } from '@jouzen/ecom-components/v3/types/stockStatus';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { type ReactNode } from 'react';

import RichText from '@/app/components/RichText';
import usePeakSeasonOOSMessage from '@/app/hooks/usePeakSeasonOOSMessage';

const OuraRetailerLink = (chunks: ReactNode) => (
  <BodyLink
    className="text-current"
    href="https://support.ouraring.com/hc/en-us/articles/18852337038227-Retail-Locations"
    target="_blank"
  >
    {chunks}
  </BodyLink>
);

/**
 * Configuration for shipping delay messaging in Statsig
 */
export interface ShippingDelayConfig {
  /** Number of time units (e.g., 2 for "2 weeks") */
  number: number | string;
  /** Time unit description (e.g., "week", "weeks") */
  timeframe: string;
}

export interface RingInventoryMessageProps {
  readonly selectedRingSize?: string;
  readonly shippingDelayConfig?: ShippingDelayConfig | undefined;
  readonly stockStatus: StockStatus;
}

const RingInventoryMessage = ({
  selectedRingSize,
  shippingDelayConfig,
  stockStatus,
}: RingInventoryMessageProps) => {
  const showPeakSeasonOOSMessage = usePeakSeasonOOSMessage();
  const { number, timeframe } = shippingDelayConfig ?? {
    number: 0,
    timeframe: '',
  };

  switch (stockStatus) {
    case LOW_STOCK:
      return number && timeframe ? (
        <span
          className="mt-2 flex items-center gap-x-2"
          data-cy="low-stock-message"
        >
          <LowStockIcon className="text-red-600" />
          <Typography className="text-red-600" variant="eyebrow-small">
            <RichText
              messageKey="shipping_delay_timeframe"
              tValues={{ number, timeframe }}
            />
          </Typography>
        </span>
      ) : null;
    case OUT_OF_STOCK:
      return selectedRingSize && showPeakSeasonOOSMessage ? (
        <Typography
          className="mt-2 text-red-600"
          variant="eyebrow-small"
          data-cy="out-of-stock-message"
        >
          <RichText
            messageKey="out_of_stock_message"
            tValues={{ size: selectedRingSize, link: OuraRetailerLink }}
          />
        </Typography>
      ) : null;
    default:
      return null;
  }
};

export default RingInventoryMessage;
